import { FsMatchTrack } from "db/model/match/Track/FsMatchTrack";
import { RoundsTrack } from "model/Match/Set/Rounds/Track/RoundsTrack";
import { PointsDistribution } from "model/Match/Set/PointsDistribution";
import { Scores } from "model/Scores";
import {
  getLivesLost,
  getMatchingLivesRules,
  LivesRules,
} from "model/Match/Set/Knockout/LivesRules";
import { mapRoundsTrack } from "../Rounds/FsRoundsTrack";
import { Positions } from "model/Positions";
import { getMatchTrackPbForDriverIds } from "model/Match/Track/MatchTrack";
import { getRosterDriverIds, getRosterId, Roster } from "model/Roster/Roster";
import { RosterScope } from "model/Roster/RosterScope";
import { RoundsRound } from "model/Match/Set/Rounds/Track/RoundsRound";

export const mapRoundsKnockoutTracks = (
  fsModels: FsMatchTrack[],
  rosters: Roster[],
  rosterScope: RosterScope,
  runningLives: Scores,
  livesRules: LivesRules[],
  pointsDist: PointsDistribution | undefined,
  roundsPerTrack: number,
  tiebreaker: "Best" | "LastPos"
) => {
  const tracks: RoundsTrack[] = [];
  for (let i = 0; i < fsModels.length; i++) {
    const fsModel = fsModels[i];
    const trackScores = {};
    const numDriversLeft = Object.keys(runningLives).filter(
      (l) => runningLives[l] > 0
    ).length;

    const numLivesLeft = Object.values(runningLives).reduce((a, b) => a + b, 0);
    const points = pointsDist ?? {
      dnfPoints: 0,
      distribution: [],
    };

    // todo - improve this behaviour
    if (!pointsDist) {
      let p = numDriversLeft;
      while (p > 0) {
        points.distribution.push(p);
        p--;
      }
    }

    // Filter rosters to only include those with positive lives
    const activeRosters = rosters.filter((roster) => {
      const rosterId = getRosterId(roster);
      return rosterId && runningLives[rosterId] > 0;
    });

    const track = mapRoundsTrack(
      fsModel,
      activeRosters,
      rosterScope,
      trackScores,
      points,
      roundsPerTrack
    );
    if (!track) {
      continue;
    }

    tracks.push(track);

    if (track.isComplete) {
      const numDriversLeft = Object.keys(runningLives).filter(
        (l) => runningLives[l] > 0
      ).length;

      const matchingRules = getMatchingLivesRules(
        livesRules,
        numDriversLeft,
        numLivesLeft,
        i + 1
      );

      if (!matchingRules) {
        continue;
      }

      let teamPositions: Positions;
      if (tiebreaker === "Best") {
        const bestTimes: { [rosterId: string]: number | undefined } = {};
        for (let i = 0; i < rosters.length; i++) {
          const roster = rosters[i];
          const rosterId = getRosterId(roster);
          const driverIds = getRosterDriverIds(roster, rosterScope);
          if (!rosterId || !driverIds) {
            continue;
          }

          bestTimes[rosterId] = getMatchTrackPbForDriverIds(track, driverIds);
        }

        teamPositions = getTeamPositionsBest(trackScores, bestTimes);
      } else {
        teamPositions = getTeamPositionsLastPos(
          trackScores,
          track.rounds.at(-1)
        );
      }

      for (let i = 0; i < rosters.length; i++) {
        const roster = rosters[i];
        const rosterId = getRosterId(roster);
        if (!rosterId) {
          continue;
        }

        const livesAtStart = runningLives[rosterId];
        if (livesAtStart <= 0) {
          continue;
        }

        const livesLost = getLivesLost(
          matchingRules,
          numDriversLeft,
          teamPositions[rosterId]
        );
        const remainingLives = livesAtStart - livesLost;
        if (remainingLives <= 0) {
          runningLives[rosterId] = numDriversLeft * -1;
        } else {
          runningLives[rosterId] = livesAtStart - livesLost;
        }
      }
    }
  }

  return tracks;
};

const getTeamPositionsBest = (
  scores: Scores,
  bestTimes: { [teamId: string]: number | undefined }
) => {
  const rosterIds = Object.keys(scores);
  const positions: Positions = {};
  const sortedRosterIds = rosterIds.sort((a, b) =>
    compareScoresBest(scores, bestTimes, a, b)
  );

  for (let i = 0; i < sortedRosterIds.length; i++) {
    const rosterId = sortedRosterIds[i];
    const previousRosterId = i > 0 ? sortedRosterIds[i - 1] : undefined;
    if (
      !!previousRosterId &&
      compareScoresBest(scores, bestTimes, rosterId, previousRosterId) === 0
    ) {
      positions[rosterId] = positions[previousRosterId];
    } else {
      positions[rosterId] = i + 1;
    }
  }

  return positions;
};

const compareScoresBest = (
  scores: Scores,
  bestTimes: { [teamId: string]: number | undefined },
  a: string,
  b: string
) => {
  const aScore = scores[a];
  const bScore = scores[b];

  if (aScore === bScore) {
    const aBestTime = bestTimes[a];
    const bBestTime = bestTimes[b];

    if (!bBestTime) {
      return -1;
    }

    if (!aBestTime) {
      return 1;
    }

    return aBestTime - bBestTime;
  }

  if (!bScore) {
    return -1;
  }

  if (!aScore) {
    return 1;
  }

  return bScore - aScore;
};

const getTeamPositionsLastPos = (
  scores: Scores,
  lastRound: RoundsRound | undefined
) => {
  const rosterIds = Object.keys(scores);
  const positions: Positions = {};
  const sortedRosterIds = rosterIds.sort((a, b) =>
    compareScoresRound(scores, lastRound, a, b)
  );

  for (let i = 0; i < sortedRosterIds.length; i++) {
    const rosterId = sortedRosterIds[i];
    const previousRosterId = i > 0 ? sortedRosterIds[i - 1] : undefined;
    if (
      !!previousRosterId &&
      compareScoresRound(scores, lastRound, rosterId, previousRosterId) === 0
    ) {
      positions[rosterId] = positions[previousRosterId];
    } else {
      positions[rosterId] = i + 1;
    }
  }

  return positions;
};

const compareScoresRound = (
  scores: Scores,
  round: RoundsRound | undefined,
  a: string,
  b: string
) => {
  const aScore = scores[a];
  const bScore = scores[b];

  if (aScore === bScore) {
    if (!round) {
      return 0;
    }
    const aLastPos = round.times.find((t) => t.driverId === a)?.position;
    const bLastPos = round.times.find((t) => t.driverId === b)?.position;
    if (!bLastPos) {
      return -1;
    }

    if (!aLastPos) {
      return 1;
    }

    return aLastPos - bLastPos;
  }

  if (!bScore) {
    return -1;
  }

  if (!aScore) {
    return 1;
  }

  return bScore - aScore;
};
